import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Truck,
  Package,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Settings,
  Plus,
  Minus,
  Trash2,
  Search,
  X,
} from 'lucide-react';

const isVan = (w) => w.type === 'van' || w.warehouse_type === 'carrinha';

export default function VanReplenishment() {
  const { user } = useContext(AuthContext);

  const [replenishmentQueue, setReplenishmentQueue] = useState({ warehouses: [], total_warehouses: 0 });
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWarehouses, setExpandedWarehouses] = useState({});
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [itemSearch, setItemSearch] = useState('');
  // Per-item quantity to include in the batch TAR: {itemId: qty}
  const [quantities, setQuantities] = useState({});

  // Min Stock Config State
  const [showMinStockModal, setShowMinStockModal] = useState(false);
  const [selectedVanForConfig, setSelectedVanForConfig] = useState(null);
  const [minStockConfig, setMinStockConfig] = useState([]);
  const [loadingMinStock, setLoadingMinStock] = useState(false);
  const [searchProduct, setSearchProduct] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [newMinQuantity, setNewMinQuantity] = useState(1);

  // Batch TAR modal
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchWarehouse, setBatchWarehouse] = useState(null);
  const [sourceWarehouse, setSourceWarehouse] = useState('');
  const [processing, setProcessing] = useState(false);

  // Clear item modal
  const [itemToClear, setItemToClear] = useState(null);
  const [clearing, setClearing] = useState(false);

  const fetchReplenishmentQueue = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedWarehouse !== 'all' ? { warehouse_code: selectedWarehouse } : {};
      const response = await axios.get(`${API}/replenishment`, { params });
      setReplenishmentQueue(response.data);

      const expanded = {};
      const qtys = {};
      response.data.warehouses?.forEach(wh => {
        expanded[wh.warehouse_code] = true;
        wh.items.forEach(item => { qtys[item.id] = item.pending_quantity; });
      });
      setExpandedWarehouses(expanded);
      setQuantities(qtys);
    } catch (error) {
      console.error('Error fetching replenishment queue:', error);
      toast.error('Erro ao carregar fila de reposição');
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouse]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/warehouses`);
      setWarehouses(response.data.warehouses || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  }, []);

  const fetchMinStockConfig = async (warehouseCode) => {
    try {
      setLoadingMinStock(true);
      const response = await axios.get(`${API}/warehouses/${warehouseCode}/min-stock`);
      setMinStockConfig(response.data.items || []);
    } catch (error) {
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoadingMinStock(false);
    }
  };

  const searchProducts = async (query) => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    try {
      setSearchingProducts(true);
      const response = await axios.get(`${API}/products`, { params: { search: query, page_size: 10 } });
      setSearchResults(response.data.items || []);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setSearchingProducts(false);
    }
  };

  const addMinStockConfig = async (productSku) => {
    if (!selectedVanForConfig || !productSku) return;
    try {
      await axios.post(`${API}/warehouses/${selectedVanForConfig.code}/min-stock`, null, {
        params: { product_sku: productSku, min_quantity: newMinQuantity }
      });
      toast.success('Stock mínimo configurado');
      fetchMinStockConfig(selectedVanForConfig.code);
      setSearchProduct('');
      setSearchResults([]);
      setNewMinQuantity(1);
    } catch (error) { toast.error('Erro ao configurar stock mínimo'); }
  };

  const deleteMinStockConfig = async (productSku) => {
    if (!selectedVanForConfig) return;
    try {
      await axios.delete(`${API}/warehouses/${selectedVanForConfig.code}/min-stock/${productSku}`);
      toast.success('Configuração removida');
      fetchMinStockConfig(selectedVanForConfig.code);
    } catch (error) { toast.error('Erro ao remover configuração'); }
  };

  const openConfigModal = (warehouse) => {
    setSelectedVanForConfig(warehouse);
    setShowMinStockModal(true);
    fetchMinStockConfig(warehouse.code || warehouse.warehouse_code);
  };

  const setQty = (itemId, value, max) => {
    const v = Math.max(0, Math.min(max, value));
    setQuantities(prev => ({ ...prev, [itemId]: v }));
  };

  // Non-van warehouses eligible as TAR source (excluding the destination van)
  const sourceOptions = (destCode) =>
    warehouses
      .filter(w => !isVan(w) && w.code !== destCode)
      .sort((a, b) => (a.code === '001' ? -1 : b.code === '001' ? 1 : a.code.localeCompare(b.code)));

  const defaultSource = (destCode) => {
    const opts = sourceOptions(destCode);
    // Use reference_warehouse (canonical) with legacy fallback
    const userRefWh = user?.reference_warehouse || user?.default_warehouse;
    if (userRefWh && opts.some(w => w.code === userRefWh)) return userRefWh;
    if (opts.some(w => w.code === '001')) return '001';
    return opts[0]?.code || '';
  };

  const openBatchModal = (warehouse) => {
    const included = warehouse.items.filter(it => (quantities[it.id] || 0) > 0);
    if (included.length === 0) {
      toast.error('Nenhum artigo com quantidade > 0 para repor');
      return;
    }
    setBatchWarehouse(warehouse);
    setSourceWarehouse(defaultSource(warehouse.warehouse_code));
    setShowBatchModal(true);
  };

  const applyBatch = async () => {
    if (!batchWarehouse || !sourceWarehouse) return;
    const items = batchWarehouse.items
      .filter(it => (quantities[it.id] || 0) > 0)
      .map(it => ({ item_id: it.id, quantity: quantities[it.id] }));
    if (items.length === 0) return;
    try {
      setProcessing(true);
      const res = await axios.post(
        `${API}/replenishment/warehouse/${batchWarehouse.warehouse_code}/apply-batch`,
        { source_warehouse: sourceWarehouse, items },
      );
      toast.success(res.data.message || `TAR criada: ${res.data.tar_reference}`);
      if (res.data.warnings?.length) {
        res.data.warnings.forEach(w => toast.warning(w));
      }
      setShowBatchModal(false);
      fetchReplenishmentQueue();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao criar TAR no SAGE';
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const clearItem = async () => {
    if (!itemToClear) return;
    try {
      setClearing(true);
      await axios.post(`${API}/replenishment/${itemToClear.id}/mark-replenished`, null, {
        params: { quantity: itemToClear.pending_quantity },
      });
      toast.success(`${itemToClear.product_sku} removido das sugestões (sem TAR)`);
      setItemToClear(null);
      fetchReplenishmentQueue();
    } catch (error) {
      toast.error('Erro ao limpar item');
    } finally {
      setClearing(false);
    }
  };

  const toggleWarehouse = (warehouseCode) => {
    setExpandedWarehouses(prev => ({ ...prev, [warehouseCode]: !prev[warehouseCode] }));
  };

  useEffect(() => { fetchWarehouses(); }, [fetchWarehouses]);
  useEffect(() => { fetchReplenishmentQueue(); }, [fetchReplenishmentQueue]);
  useEffect(() => {
    const timer = setTimeout(() => { searchProducts(searchProduct); }, 300);
    return () => clearTimeout(timer);
  }, [searchProduct]);

  const totalItems = replenishmentQueue.warehouses?.reduce((sum, wh) => sum + wh.total_items, 0) || 0;
  const totalSkus = replenishmentQueue.warehouses?.reduce((sum, wh) => sum + wh.items.length, 0) || 0;
  const vans = warehouses.filter(isVan);

  const batchSummary = (warehouse) => {
    const included = warehouse.items.filter(it => (quantities[it.id] || 0) > 0);
    const units = included.reduce((s, it) => s + (quantities[it.id] || 0), 0);
    return { count: included.length, units };
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-8" data-testid="van-replenishment-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Truck className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                Reposição
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Artigos consumidos pelos técnicos</p>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchReplenishmentQueue}
              className="h-10 w-10 shrink-0"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 space-y-4">
        {/* Filter */}
      <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
        <SelectTrigger className="h-11" data-testid="filter-warehouse">
          <Truck className="h-4 w-4 mr-2 text-muted-foreground" />
          <SelectValue placeholder="Filtrar carrinha" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas as carrinhas</SelectItem>
          {vans.map(wh => (
            <SelectItem key={wh.code} value={wh.code}>{wh.name || wh.code}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Item search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Pesquisar artigo (SKU ou nome)..."
          value={itemSearch}
          onChange={(e) => setItemSearch(e.target.value)}
          className="h-11 pl-10"
          data-testid="replenishment-search-input"
        />
      </div>

      {/* Summary Stats — inline, sóbrio */}
      <div className="flex items-center gap-6 text-sm text-slate-600 border-b border-slate-200 pb-3">
        <span><span className="text-lg font-bold tabular-nums text-slate-900">{replenishmentQueue.total_warehouses}</span> Carrinhas</span>
        <span><span className="text-lg font-bold tabular-nums text-slate-900">{totalItems}</span> Itens</span>
        <span><span className="text-lg font-bold tabular-nums text-slate-900">{totalSkus}</span> SKUs</span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="py-12 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">A carregar...</p>
        </div>
      ) : replenishmentQueue.warehouses?.length === 0 ? (
        <div className="py-12 text-center">
          <CheckCheck className="h-14 w-14 mx-auto text-green-500 mb-3" />
          <h3 className="text-lg font-semibold">Tudo reposto!</h3>
          <p className="text-sm text-muted-foreground mt-1">Não há itens pendentes de reposição.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {replenishmentQueue.warehouses?.map(warehouse => {
            const summary = batchSummary(warehouse);
            const sq = itemSearch.trim().toLowerCase();
            const visibleItems = !sq ? warehouse.items : warehouse.items.filter(it =>
              (it.product_sku || '').toLowerCase().includes(sq)
              || (it.product_name || '').toLowerCase().includes(sq));
            if (sq && visibleItems.length === 0) return null;
            return (
              <div key={warehouse.warehouse_code} className="bg-white rounded-lg border overflow-hidden" data-testid={`warehouse-card-${warehouse.warehouse_code}`}>
                {/* Warehouse Header */}
                <button
                  onClick={() => toggleWarehouse(warehouse.warehouse_code)}
                  className="w-full flex items-center justify-between p-4 active:bg-slate-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {expandedWarehouses[warehouse.warehouse_code]
                      ? <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                      : <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />}
                    <Truck className="h-5 w-5 text-orange-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-semibold text-sm sm:text-base truncate">
                        {warehouse.warehouse_name || warehouse.warehouse_code}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {warehouse.items.length} produtos · {warehouse.total_items} un.
                      </p>
                    </div>
                  </div>
                  <Badge variant="destructive" className="text-xs flex-shrink-0 ml-2">
                    {warehouse.total_items}
                  </Badge>
                </button>

                {expandedWarehouses[warehouse.warehouse_code] && (
                  <>
                    {/* Items */}
                    <div className="border-t divide-y">
                      {visibleItems.map(item => {
                        const qty = quantities[item.id] ?? item.pending_quantity;
                        return (
                          <div key={item.id} className={`p-3 flex items-center gap-2 ${qty === 0 ? 'opacity-50' : ''}`} data-testid={`item-row-${item.id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="font-mono text-xs text-slate-500">{item.product_sku}</p>
                              <p className="text-sm font-medium truncate">{item.product_name}</p>
                              <div className="flex gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                                <span>Pendente: <strong className="text-red-600">{item.pending_quantity}</strong></span>
                                <span>Reposto: <strong className="text-green-600">{item.quantity_replenished || 0}</strong></span>
                                {item.last_tar_reference && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100 text-[10px]" title="Última TAR criada no SAGE">
                                    {item.last_tar_reference}
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Quantity stepper (0 = não incluir na TAR) */}
                            <div className="flex items-center flex-shrink-0">
                              <button
                                onClick={() => setQty(item.id, qty - 1, item.pending_quantity)}
                                className="h-10 w-10 flex items-center justify-center rounded-l-lg border border-slate-200 bg-slate-50 active:bg-red-50"
                                data-testid={`qty-minus-${item.id}`}
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <Input
                                type="number"
                                min={0}
                                max={item.pending_quantity}
                                value={qty}
                                onChange={(e) => setQty(item.id, parseInt(e.target.value) || 0, item.pending_quantity)}
                                className="h-10 w-14 text-center font-bold font-mono rounded-none border-x-0 px-0"
                                data-testid={`qty-input-${item.id}`}
                              />
                              <button
                                onClick={() => setQty(item.id, qty + 1, item.pending_quantity)}
                                className="h-10 w-10 flex items-center justify-center rounded-r-lg border border-slate-200 bg-slate-50 active:bg-green-50"
                                data-testid={`qty-plus-${item.id}`}
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                            {/* Clear item (stop suggesting, no TAR) */}
                            <button
                              onClick={() => setItemToClear(item)}
                              className="h-10 w-10 flex items-center justify-center text-slate-400 hover:text-red-500 active:bg-red-50 rounded-lg flex-shrink-0"
                              title="Limpar — deixa de sugerir (sem TAR)"
                              data-testid={`clear-item-${item.id}`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Footer actions: single batch TAR */}
                    <div className="flex gap-2 p-3 border-t bg-slate-50">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-11 text-xs"
                        onClick={() => openConfigModal({ code: warehouse.warehouse_code, name: warehouse.warehouse_name })}
                        data-testid={`config-btn-${warehouse.warehouse_code}`}
                      >
                        <Settings className="h-3.5 w-3.5 mr-1" />
                        Config
                      </Button>
                      <Button
                        className="h-11 text-sm flex-1 bg-green-600 hover:bg-green-700 active:bg-green-800"
                        disabled={summary.count === 0}
                        onClick={() => openBatchModal(warehouse)}
                        data-testid={`batch-tar-btn-${warehouse.warehouse_code}`}
                      >
                        <Check className="h-4 w-4 mr-1.5" />
                        Criar TAR única ({summary.count} artigos · {summary.units} un.)
                      </Button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Batch TAR Confirm Modal */}
      <Dialog open={showBatchModal} onOpenChange={setShowBatchModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle>Criar TAR única no SAGE</DialogTitle>
            <DialogDescription>
              Destino: <strong className="font-mono">{batchWarehouse?.warehouse_name || batchWarehouse?.warehouse_code}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 min-w-0">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Armazém de origem</label>
              <select
                value={sourceWarehouse}
                onChange={(e) => setSourceWarehouse(e.target.value)}
                className="w-full h-12 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                data-testid="source-warehouse-select"
              >
                {batchWarehouse && sourceOptions(batchWarehouse.warehouse_code).map(w => (
                  <option key={w.code} value={w.code}>
                    {w.code} — {w.name}{w.code === user?.default_warehouse ? ' (o teu armazém)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Summary of lines */}
            <div className="border rounded-lg divide-y max-h-[240px] overflow-y-auto w-full">
              {batchWarehouse?.items
                .filter(it => (quantities[it.id] || 0) > 0)
                .map(it => (
                  <div key={it.id} className="p-2.5 flex items-center justify-between gap-2 text-sm min-w-0">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs text-slate-500 truncate">{it.product_sku}</p>
                      <p className="truncate">{it.product_name}</p>
                    </div>
                    <Badge variant="secondary" className="flex-shrink-0 font-mono">{quantities[it.id]}x</Badge>
                  </div>
                ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Será criada <strong>uma única TAR</strong> no SAGE com todos os artigos acima, movendo o stock de <strong className="font-mono">{sourceWarehouse}</strong> para a carrinha. Artigos sem stock suficiente na origem são bloqueados.
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" className="h-12 w-full sm:w-auto" onClick={() => setShowBatchModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={applyBatch}
              disabled={processing || !sourceWarehouse}
              className="h-12 w-full sm:w-auto bg-green-600 hover:bg-green-700 active:bg-green-800"
              data-testid="confirm-batch-tar-btn"
            >
              {processing ? <RefreshCw className="h-5 w-5 mr-2 animate-spin" /> : <Check className="h-5 w-5 mr-2" />}
              Criar TAR no SAGE
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Item Confirm Modal */}
      <Dialog open={!!itemToClear} onOpenChange={(open) => !open && setItemToClear(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle>Limpar item da reposição?</DialogTitle>
            <DialogDescription>
              <span className="font-mono">{itemToClear?.product_sku}</span> · {itemToClear?.product_name}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            O item deixa de ser sugerido ({itemToClear?.pending_quantity} un. pendentes) e <strong>não é criada TAR no SAGE</strong>. Voltará a aparecer apenas se houver novos consumos.
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" className="h-11 w-full sm:w-auto" onClick={() => setItemToClear(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              className="h-11 w-full sm:w-auto"
              onClick={clearItem}
              disabled={clearing}
              data-testid="confirm-clear-item-btn"
            >
              {clearing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Limpar (sem TAR)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Min Stock Config Modal */}
      <Dialog open={showMinStockModal} onOpenChange={setShowMinStockModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle>Stock Mínimo</DialogTitle>
            <DialogDescription>
              {selectedVanForConfig?.name || selectedVanForConfig?.code}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Search */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Adicionar Produto</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar por SKU ou nome..."
                  value={searchProduct}
                  onChange={(e) => setSearchProduct(e.target.value)}
                  className="pl-10 h-11"
                  data-testid="search-product-input"
                />
              </div>
              {searchingProducts && <p className="text-xs text-muted-foreground">A pesquisar...</p>}
              {searchResults.length > 0 && (
                <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
                  {searchResults.map(product => (
                    <button
                      key={product.sku}
                      className="w-full p-3 active:bg-slate-50 text-left flex items-center justify-between gap-2"
                      onClick={() => addMinStockConfig(product.sku)}
                      data-testid={`search-result-${product.sku}`}
                    >
                      <div className="min-w-0">
                        <p className="font-mono text-xs">{product.sku}</p>
                        <p className="text-sm truncate">{product.name}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Input
                          type="number"
                          min={1}
                          value={newMinQuantity}
                          onChange={(e) => { e.stopPropagation(); setNewMinQuantity(parseInt(e.target.value) || 1); }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-16 h-9 text-center"
                          data-testid={`min-qty-input-${product.sku}`}
                        />
                        <Plus className="h-4 w-4 text-green-600" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Current configs */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Configurações Actuais</label>
              {loadingMinStock ? (
                <div className="text-center py-4"><RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
              ) : minStockConfig.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">Nenhum stock mínimo configurado.</p>
              ) : (
                <div className="border rounded-lg divide-y">
                  {minStockConfig.map(config => (
                    <div key={config.product_sku} className="p-3 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-mono text-xs">{config.product_sku}</p>
                        <p className="text-sm truncate">{config.product_name}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge variant="secondary" className="text-sm">{config.min_quantity}</Badge>
                        <button
                          onClick={() => deleteMinStockConfig(config.product_sku)}
                          className="h-9 w-9 flex items-center justify-center text-red-500 active:bg-red-50 rounded-lg"
                          data-testid={`delete-config-${config.product_sku}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button className="h-11 w-full" onClick={() => setShowMinStockModal(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
